import { Icon, Text, cn } from '@eluve/components';
import type { PlanActivity } from '@eluve/plan-shapes';

import { MarkdownProse } from './MarkdownProse';
import type { Accent } from './accents';
import { formatCadence, formatDosage } from './format';
import { Eyebrow, MonoChip, Panel, PanelInset } from './panel';

// ActivityList — the shared render primitive for assigned activities (Plan
// Activities): one row per activity with its dosing/frequency chips, form
// cues, and patient-facing instructions. `surface` flips the row tone to keep
// figure/ground contrast with the parent (inset rows on a Panel, panel rows
// on an inset).
export function ActivityList({
  activities,
  accent,
  surface = 'inset',
  className,
}: {
  activities: PlanActivity[];
  accent: Accent;
  surface?: 'inset' | 'panel';
  className?: string;
}) {
  const Row = surface === 'panel' ? Panel : PanelInset;
  return (
    <ul className={cn('space-y-2', className)}>
      {activities.map((activity, i) => (
        <li key={i}>
          <Row className="px-3.5 py-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <Icon
                name="Dumbbell"
                size="auto"
                className={cn('size-3.5 shrink-0', accent.text)}
              />
              <Text inline>{activity.name}</Text>
              {formatDosage(activity.dosage).map((part) => (
                <MonoChip key={part} className={accent.tile}>
                  {part}
                </MonoChip>
              ))}
              {activity.frequency && (
                <MonoChip className={accent.tile}>
                  <Icon name="CalendarClock" size="auto" />
                  {formatCadence(activity.frequency)}
                </MonoChip>
              )}
            </div>
            {activity.description && (
              <MarkdownProse
                content={activity.description}
                className="mt-1 text-xs text-muted-foreground"
              />
            )}
            {activity.patientInstructions && (
              <div className={cn('mt-1.5 border-l-2 pl-2.5', accent.text)}>
                <Eyebrow>Patient instructions</Eyebrow>
                <MarkdownProse
                  content={activity.patientInstructions}
                  className="mt-0.5 text-xs text-foreground"
                />
              </div>
            )}
          </Row>
        </li>
      ))}
    </ul>
  );
}
